import React from 'react';
import { Link, useLocation } from 'react-router-dom';

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
  className?: string;
}

const Separator = () => (
  <svg
    className="w-4 h-4 text-gray-300 dark:text-slate-600 flex-shrink-0"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items, className = '' }) => {
  const location = useLocation();

  // If no items provided, generate from current path
  const breadcrumbItems: BreadcrumbItem[] = items || [];

  // Don't show breadcrumbs on dashboard
  if (location.pathname === '/' && !items) {
    return null;
  }

  if (breadcrumbItems.length === 0) {
    return null;
  }

  const lastItem = breadcrumbItems[breadcrumbItems.length - 1];
  const middleItems = breadcrumbItems.slice(0, -1);

  return (
    <nav className={`flex items-center text-sm min-w-0 ${className}`} aria-label="Breadcrumb">
      {/* Home Icon */}
      <Link
        to="/"
        className="text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 transition-colors flex-shrink-0"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      </Link>

      {/* Middle items — always shown, no mobile ellipsis collapse */}
      {middleItems.length > 0 && (
        <div className="flex items-center">
          {middleItems.map((item, index) => (
            <React.Fragment key={index}>
              <div className="mx-1.5">
                <Separator />
              </div>
              {item.path ? (
                <Link
                  to={item.path}
                  className="text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300 transition-colors py-1 px-1 -my-1 -mx-1 rounded hover:bg-gray-100 dark:hover:bg-slate-800/50"
                >
                  {item.label}
                </Link>
              ) : (
                <span className="text-gray-500 dark:text-slate-400">
                  {item.label}
                </span>
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Last Item (always shown) - truncates on mobile, full text on desktop */}
      <div className="mx-1.5 flex-shrink-0">
        <Separator />
      </div>
      <span className="text-gray-900 dark:text-white font-medium min-w-0 truncate sm:whitespace-normal sm:overflow-visible">
        {lastItem.label}
      </span>
    </nav>
  );
};

export default Breadcrumbs;
